import { useEffect, useRef } from "react";

/**
 * Debounced auto-save. Calls `onSave` ~`delay` ms after `signature` stops
 * changing — but only for genuine USER edits:
 *   • the first `signature` (initial load / hydrate) becomes the baseline and
 *     never triggers a save;
 *   • a save runs only when `signature` differs from the last saved one;
 *   • saves never overlap — a change that lands mid-save is flushed right after.
 *
 * `enabled=false` pauses saving (e.g. no submission id yet, or the section is
 * invalid) WITHOUT losing the baseline, so toggling it back on doesn't
 * re-save unchanged data.
 *
 * Typing stays snappy: this only schedules a timeout, so the network work runs
 * after the debounce, off the keystroke path. Keep `onSave` stable
 * (useCallback) — it's read through a ref, so identity changes don't reset the
 * debounce.
 */
export function useDebouncedAutosave(
    signature: string,
    onSave: () => unknown | Promise<unknown>,
    options?: { delay?: number; enabled?: boolean },
): void {
    const delay = options?.delay ?? 800;
    const enabled = options?.enabled ?? true;

    const savedSig = useRef<string | null>(null);
    const latestSig = useRef(signature);
    const running = useRef(false);
    const saveRef = useRef(onSave);
    const enabledRef = useRef(enabled);

    // Keep the latest signature, save callback, and enabled flag in refs
    // (updated post-render, not during render) so the debounced flush always
    // sees current values. `enabledRef` matters specifically for a flush
    // loop already in flight: `enabled` re-checked via the loop's own
    // closure would still read whatever it was when THAT flush started,
    // so disabling mid-save (e.g. a calculation starting while a save is
    // already in progress) wouldn't stop the loop from queuing one more
    // save the instant the in-flight one resolves — this ref makes that
    // check live instead.
    useEffect(() => {
        latestSig.current = signature;
        saveRef.current = onSave;
        enabledRef.current = enabled;
    });

    useEffect(() => {
        if (!enabled) return;
        // First signature = baseline → don't save on load/hydrate.
        if (savedSig.current === null) {
            savedSig.current = signature;
            return;
        }
        if (signature === savedSig.current) return;

        const flush = async () => {
            if (running.current) return; // in-flight save loops to catch up
            running.current = true;
            try {
                while (enabledRef.current && latestSig.current !== savedSig.current) {
                    const snapshot = latestSig.current;
                    await saveRef.current();
                    savedSig.current = snapshot;
                }
            } finally {
                running.current = false;
            }
        };

        const timer = setTimeout(flush, delay);
        return () => clearTimeout(timer);
    }, [signature, enabled, delay]);
}
